import React from 'react';
import { useTheme } from 'styled-components';
import styled from 'styled-components/macro';
import { observer } from 'mobx-react';
import QRCode from 'react-qr-code';

import { useDataStore } from 'state/DataStore';

import ScrollArea from 'components/ScrollArea';
import Button from 'components/Button';
import joinPath from 'utilities/joinPath';


const Shortlist = observer(() => {
    const dataStore = useDataStore();
    const { shortlistStore, appSettings } = dataStore;
    const theme = useTheme();
    const hideEmailButton = appSettings?.hideEmailShortlistButton ?? false;

    const shortlistedUnits = shortlistStore.shortlistedUnitsArray.map(unitId => {
        const unit = dataStore.getItem('units', 'id', unitId);

        if (!unit) return null;

        const unitType = dataStore.getItem('unit_types', 'id', unit.acf?.unit_type);

        return { unit, unitType };
    }).filter(Boolean);

    const handleRemoveFromShortlist = (unitId) => {
        shortlistStore.removeFromShortlist(unitId);
    };

    const handleClearAll = () => {
        shortlistStore.clearShortlist();
    };

    // Generate QR code URL
    const baseUrl = process.env.REACT_APP_API_ROOT || window.location.origin;
    const unitIds = shortlistStore.shortlistedUnitsArray.join(',');
    const emailUrl = joinPath(baseUrl, '/email-shortlist/');
    const qrCodeUrl = unitIds ? `${emailUrl}?unit_ids=${unitIds}` : emailUrl;

    return (
        <Container>
            <Header>
                <Title>My Shortlist</Title>

                {shortlistedUnits.length > 0 && (
                    <ClearButton onClick={handleClearAll}>
                        Clear shortlist
                    </ClearButton>
                )}
            </Header>


            {shortlistedUnits.length === 0 ? (
                <EmptyState>
                    <EmptyText>No apartments in your shortlist yet</EmptyText>
                    <EmptySubtext>Start browsing and add apartments to your shortlist</EmptySubtext>
                </EmptyState>
            ) : (
                <Content>
                    <GridColumn>
                        <ScrollArea autoHide={false} fadeHeight='3rem'>
                            <Grid>
                                {shortlistedUnits.map(({ unit, unitType }) => (
                                    <GridItem key={unit.id}>
                                        <FloorplanImage>
                                            <img src={unitType?.acf?.floorplan?.sizes?.medium} alt={`Floorplan for ${unit.title.rendered}`} />
                                        </FloorplanImage>

                                        <Info>
                                            <UnitName className='h6'>Apartment {unit.title.rendered}</UnitName>

                                            <RemoveButton
                                                onClick={() => handleRemoveFromShortlist(unit.id)}
                                                title="Remove from shortlist"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19" fill="none">
                                                    <path d="M8.67692 8.96235V13.1624M11.3436 8.96235V13.1624M14.6769 5.46235V15.2624C14.6769 15.6337 14.5364 15.9898 14.2864 16.2523C14.0363 16.5149 13.6972 16.6624 13.3436 16.6624H6.67692C6.3233 16.6624 5.98416 16.5149 5.73411 16.2523C5.48406 15.9898 5.34359 15.6337 5.34359 15.2624V5.46235M4.01025 5.46235H16.0103M7.34359 5.46235V4.06235C7.34359 3.69105 7.48406 3.33495 7.73411 3.0724C7.98416 2.80985 8.3233 2.66235 8.67692 2.66235H11.3436C11.6972 2.66235 12.0363 2.80985 12.2864 3.0724C12.5364 3.33495 12.6769 3.69105 12.6769 4.06235V5.46235" stroke="#DE4D4D" stroke-linecap="round" stroke-linejoin="round" />
                                                </svg>
                                            </RemoveButton>
                                        </Info>
                                    </GridItem>
                                ))}
                            </Grid>
                        </ScrollArea>
                    </GridColumn>

                    <QRColumn>
                        <QRColumnInner>
                            <QRCodeWrapper>
                                <QRCode
                                    value={qrCodeUrl}
                                    level="M"
                                    fgColor={theme.primary_color}
                                    bgColor="transparent"
                                />
                            </QRCodeWrapper>

                            <QRCodeDescription>
                                {hideEmailButton
                                    ? 'Scan this QR Code to get plans, brochures and inclusion information for the shortlisted apartments.'
                                    : 'Scan this QR Code, or use the button below to get plans, brochures and inclusion information for the shortlisted apartments.'}
                            </QRCodeDescription>

                            {!hideEmailButton && (
                                <ButtonContainer>
                                    <EmailButton href={qrCodeUrl} target="_blank">
                                        Email me the shortlist
                                    </EmailButton>
                                </ButtonContainer>
                            )}
                        </QRColumnInner>
                    </QRColumn>
                </Content>
            )}
        </Container>
    );
});

export default Shortlist;

const Container = styled.div`
    width: 90vw;
    max-width: 1600px;
    height: 90vh;
    max-height: 1000px;
    padding: 6rem 8rem 5rem 8rem;

    display: flex;
    flex-direction: column;
    background: white;
    overflow: hidden;
`;

const Header = styled.div`
    flex: 0 0 auto;
    display: flex;
    align-items: flex-end;
    gap: 3rem;
    width: 65%;
    margin-bottom: 1rem;
`;

const Title = styled.h2`

`;

const ClearButton = styled(Button)`
    margin-bottom: 0.6rem;
    padding: 2px 10px;
    font-size: 1.2rem;
    color: #c33a3a;
    background: #ffbba3;
    border: 0.1rem solid #ffbba3;
    border-radius: 99rem;
    
    &:hover {
        background: transparent;
        color: #c33a3a;
        border-color: #ffbba3;
    }
`;

const Content = styled.div`
    flex: 1;
    min-height: 0;
    display: flex;
    justify-content: space-between;
    align-items: stretch;
`;

const GridColumn = styled.div`
    width: 65%;
    height: 100%;
    min-height: 0;
`;

const QRColumn = styled.div`
    width: 25%;
    height: 100%;
    padding: 3rem 0;
`;

const QRColumnInner = styled.div`
    width: 100%;
    height: 100%;
    background: ${props => props.theme.secondary_color_light};
    padding: 6rem 3rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    text-align: center;
    gap: 3rem;
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    padding: 4rem;
    text-align: center;
`;

const EmptyText = styled.h3`
    margin: 0 0 1rem 0;
    font-size: 2.4rem;
    color: #666;
    font-weight: 500;
`;

const EmptySubtext = styled.p`
    margin: 0;
    font-size: 1.6rem;
    color: #999;
`;

const Grid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 3rem;
`;

const GridItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding: 1.5rem;
    background: ${props => props.theme.secondary_color_light};
    transition: all .3s ease;
`;

const Info = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
`;

const UnitName = styled.div`
`;

const RemoveButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.4rem;
    height: 2.4rem;
    padding: 0;
    color: #c33a3a;
    background: #ffbba3;
    border: .1rem solid #ffbba3;
    border-radius: .3rem;
    cursor: pointer;
    line-height: 0;
    transition: all 0.3s ease;
    
    svg {
        width: 2rem; 
        height: auto;
        stroke: currentColor;
    }

    &:hover {
        background: transparent;
    }
    
`;

const FloorplanImage = styled.div`
    background: white;
    padding: 1rem;

    img {
        width: 100%;
        aspect-ratio: 1/1;
        object-fit: contain;
    }
`;

const QRCodeDescription = styled.p`
    text-align: center;
    text-wrap: balance;
`;

const QRCodeWrapper = styled.div`
    padding: 0 3rem;

    svg {
        display: inline-block;
        vertical-align: middle;
        width: 100%;
        height: auto;
    }
`;

const ButtonContainer = styled.div`
    width: 100%;
`;  

const EmailButton = styled(Button)`
    width: 100%;
`;